import React, { useState, useContext, useRef } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import { DirectionContext } from "../App";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";

const PlaintPage = () => {
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const { navigateTo } = useContext(DirectionContext);
  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionFailed, setSubmissionFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const phoneInputRef = useRef(null);

  // 토스트 메시지 표시 함수
  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 3000); // 3초 후 토스트 숨김
  };

  // 토스트 애니메이션
  const toastVariants = {
    initial: {
      opacity: 0,
      y: 50,
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
      },
    },
    exit: {
      opacity: 0,
      y: 50,
      transition: {
        duration: 0.3,
      },
    },
  };

  // 애니메이션 변수
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    in: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  // 핸드폰 번호 변경 처리
  const handlePhoneNumberChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 11);
    setPhoneNumber(value);

    // 11자리 입력 시 키보드 닫기
    if (value.length === 11 && phoneInputRef.current) {
      phoneInputRef.current.blur();
    }
  };

  // 이름 변경 처리
  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);
  };

  // 조회 처리
  const handleSearch = async () => {
    if (name.trim().length === 0 || phoneNumber.length !== 11) {
      return;
    }

    setIsSubmitting(true);
    setSubmissionFailed(false);
    setErrorMessage("");

    try {
      // 실제 API 호출 로직 - 소장 접수 이력 조회
      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/clients/phone/${phoneNumber}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 404) {
        // 접수 이력이 없는 경우
        setSubmissionFailed(true);
        setErrorMessage("입력하신 정보로 접수된 소장을 찾을 수 없습니다.");
        showToastMessage("접수된 소장이 없습니다.");
        setIsSubmitting(false);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "소장 조회에 실패했습니다.");
      }

      const data = await response.json();

      // 이름 일치 여부 확인 (공백 제거 후 비교)
      const normalizedInputName = name.replace(/\s+/g, "").trim();
      const normalizedDataName = data.name.replace(/\s+/g, "").trim();

      if (normalizedDataName !== normalizedInputName) {
        setSubmissionFailed(true);
        setErrorMessage("입력하신 이름과 등록된 이름이 일치하지 않습니다.");
        showToastMessage("이름이 일치하지 않습니다.");
        setIsSubmitting(false);
        return;
      }

      // 결제 완료된 소장이 있는지 확인
      const hasPaymentCompleted =
        data.applications &&
        data.applications.some((app) => app.status === "PAYMENT_COMPLETED");

      if (!hasPaymentCompleted) {
        setSubmissionFailed(true);
        setErrorMessage("소장을 찾을 수 없습니다.");
        showToastMessage("소장을 찾을 수 없습니다.");
        setIsSubmitting(false);
        return;
      }

      // 접수 이력이 있는 경우 - 소장 확인 페이지로 이동
      showToastMessage("소장을 찾았습니다. 소장을 확인해주세요.");

      // 소장 데이터를 세션 스토리지에 저장
      sessionStorage.setItem("plaintData", JSON.stringify(data));

      // 소장 확인 페이지로 이동
      navigateTo("/plaintCheck");
    } catch (error) {
      setSubmissionFailed(true);
      setErrorMessage(error.message || "소장 조회에 실패했습니다.");
      showToastMessage(error.message || "소장 조회에 실패했습니다.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // 모달 확인 버튼
  const handleModalConfirm = () => {
    setShowModal(false);

    if (modalType === "success") {
      // 성공 후 메인페이지로 이동
      navigateTo("/select");
    }
  };

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content variants={contentVariants} initial="initial" animate="in">
          {/* 토스트 알림 */}
          <AnimatePresence>
            {showToast && (
              <Toast
                variants={toastVariants}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <ToastIcon>!</ToastIcon>
                {toastMessage}
              </Toast>
            )}
          </AnimatePresence>

          <div>
            <Title>소장 조회</Title>
          </div>
          <Header>
            <SubTitle>집단소송 소장 조회</SubTitle>
            <SubTitle2>
              접수된 소장을 조회하기 위해
              <br />
              이름과 휴대폰 번호를 입력해 주세요.
            </SubTitle2>
          </Header>

          <FormContainer>
            <FormGroup>
              <Label>이름을 입력해주세요.</Label>
              <PhoneInputContainer>
                <PhoneInput
                  type="text"
                  placeholder="이름을 입력해주세요"
                  value={name}
                  onChange={handleNameChange}
                />
              </PhoneInputContainer>
            </FormGroup>

            <FormGroup>
              <Label>휴대폰 번호를 입력해주세요.</Label>
              <PhoneInputContainer>
                <PhoneInput
                  type="tel"
                  placeholder="-없이 숫자만 입력"
                  value={phoneNumber}
                  onChange={handlePhoneNumberChange}
                  ref={phoneInputRef}
                />
              </PhoneInputContainer>
              <VerificationHint>
                휴대폰번호는 소장 접수 시 등록된 번호와 일치해야 합니다.
              </VerificationHint>

              {submissionFailed && (
                <VerificationFail>
                  {errorMessage ||
                    "입력하신 정보로 접수된 소장을 찾을 수 없습니다."}
                </VerificationFail>
              )}
            </FormGroup>
          </FormContainer>

          <ButtonsContainer>
            <ActionButton
              onClick={handleSearch}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={
                name.trim().length === 0 ||
                phoneNumber.length !== 11 ||
                isSubmitting
              }
            >
              {isSubmitting ? "조회 중..." : "소장 조회"}
            </ActionButton>
          </ButtonsContainer>

          {/* 모달 */}
          {showModal && (
            <ModalOverlay>
              <ModalContainer>
                <ModalContent>
                  {modalType === "success" ? (
                    <ModalText>
                      정보가 성공적으로 제출되었습니다.
                      <br />
                      다음 단계로 진행합니다.
                    </ModalText>
                  ) : (
                    <ModalText>
                      정보를 불러오는 중 오류가 발생했습니다.
                      <br />
                      다시 시도해주세요.
                    </ModalText>
                  )}
                  <ModalButton onClick={handleModalConfirm}>확인</ModalButton>
                </ModalContent>
              </ModalContainer>
            </ModalOverlay>
          )}
        </Content>
      </Container>
    </PageBackground>
  );
};

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  background-color: #f8f8f8;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    padding: 40px 20px 60px;
    background-color: #fff;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #f8f8f8;

  /* 모바일 디바이스 */
  @media (max-width: 768px) {
    min-height: auto;
    box-shadow: none;
    padding: 0 0 80px 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  /* 데스크탑 디바이스 */
  @media (min-width: 769px) {
    border-radius: 20px;
    min-height: auto;
    max-height: none;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  position: relative;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 30px 20px 40px;
  }

  @media (min-width: 769px) {
    padding: 40px 30px 40px 30px;
  }
`;

const Header = styled.div`
  width: 100%;
  margin-top: 20px;
  margin-bottom: 20px;
  color: #333;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const Title = styled.h1`
  color: #00285b;
  text-align: center;
  font-family: Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  letter-spacing: -0.34px;
  margin: 5px 0;
  ${"" /* padding-top: 10px; */}
  word-break: keep-all;
`;

const SubTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 10px;
  word-break: keep-all;
  color: #2e3840;
  text-align: left;
  width: 100%;
`;

const SubTitle2 = styled.h2`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.3;
  margin-bottom: 20px;
  word-break: keep-all;
  color: #4b5563;
  text-align: left;
  width: 100%;
`;

const FormContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 30px;
`;

const FormGroup = styled.div`
  width: 100%;
`;

const Label = styled.label`
  display: block;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #333;
`;

const PhoneInputContainer = styled.div`
  display: flex;
  width: 100%;
  position: relative;
`;

const PhoneInput = styled.input`
  flex: 1;
  height: 60px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  padding: 0 16px;
  font-size: 16px;
  background-color: white;

  &::placeholder {
    color: #999;
  }
`;

const VerificationHint = styled.p`
  font-size: 14px;
  color: #666;
  margin-top: 8px;
`;

const VerificationFail = styled.div`
  width: 100%;
  padding: 10px;
  margin-top: 10px;
  background-color: rgba(255, 76, 76, 0.1);
  color: #ff4c4c;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
`;

const ButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 60px;
  padding: 0 0px;

  @media (min-width: 769px) {
    padding: 0;
    background-color: transparent;
  }
`;

const ActionButton = styled(motion.button)`
  display: flex;
  width: 100%;
  max-width: 400px;
  height: 56px;
  padding: 0px 14px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 8px;
  background: #E31C20;
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};

  @media (min-width: 769px) {
    max-width: 430px;
    margin: 0 auto;
  }
`;

// 모달 관련 스타일
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  touch-action: none;
`;

const ModalContainer = styled.div`
  width: 90%;
  max-width: 355px;
  background-color: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
`;

const ModalContent = styled.div`
  padding: 30px 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const ModalText = styled.p`
  font-size: 16px;
  color: #333;
  text-align: left;
  margin-bottom: 30px;
  line-height: 1.5;
  width: 100%;
`;

const ModalButton = styled.button`
  width: 100%;
  height: 50px;
  background-color: #E31C20;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  align-self: center;
`;

// 토스트 알림 스타일
const Toast = styled(motion.div)`
  position: fixed;
  bottom: 30px;
  left: 12%;
  /* 데스크탑에서는 32%로 위치 조정 */
  @media (min-width: 768px) {
    left: 38.5%;
    max-width: 400px;
    min-width: 40%;
  }

  transform: translateX(-50%);
  background-color: rgba(46, 56, 64, 0.9);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-size: 14px;
  z-index: 1000;
  min-width: 300px;
  max-width: 90%;
  text-align: left;
`;

const ToastIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background-color: #ff4c4c;
  color: white;
  border-radius: 50%;
  margin-right: 12px;
  font-size: 16px;
  font-weight: bold;
`;

export default PlaintPage;
